import { lazy, Suspense, useEffect, type ComponentType, type LazyExoticComponent } from 'react';
import { EmptyState, IconGrid, Skeleton, buttonClass } from '@edil/ui';
import { AppShell } from '@/shell/AppShell';
import { Link, setPrefetcher, usePathname } from '@/router';
import { useApp } from '@/components/app-provider';

/**
 * Route table. Every screen is its own chunk, loaded on first visit (or when a link to it is
 * hovered/touched), so the first load carries only the shell and the page being opened.
 */
type Loader = () => Promise<ComponentType>;
const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K): Loader => () => load().then((m) => m[name]);

const SHELL: Record<string, Loader> = {
  '/': named(() => import('@/features/bingo/BingoRooms'), 'BingoLobbyScreen'),
  '/raffle/': named(() => import('@/features/lobby/LobbyScreen'), 'LobbyScreen'),
  '/room/': named(() => import('@/features/room/RoomScreen'), 'RoomScreen'),
  '/round/': named(() => import('@/features/round/RoundScreen'), 'RoundScreen'),
  '/verify/': named(() => import('@/features/round/VerifyScreen'), 'VerifyScreen'),
  '/bingo/': named(() => import('@/features/bingo/BingoRooms'), 'BingoLobbyScreen'),
  '/bingo/room/': named(() => import('@/features/bingo/BingoScreen'), 'BingoScreen'),
  '/bingo/verify/': named(() => import('@/features/bingo/BingoVerifyScreen'), 'BingoVerifyScreen'),
  '/tickets/': named(() => import('@/features/tickets/TicketsScreen'), 'TicketsScreen'),
  '/wallet/': named(() => import('@/features/wallet/WalletScreen'), 'WalletScreen'),
  '/profile/': named(() => import('@/features/profile/ProfileScreen'), 'ProfileScreen'),
  '/notifications/': named(() => import('@/features/notifications/NotificationsScreen'), 'NotificationsScreen'),
  '/gift/': named(() => import('@/features/gift/GiftScreen'), 'GiftScreen'),
};
const AUTH: Record<string, Loader> = {
  '/login/': named(() => import('@/features/auth/AuthScreens'), 'LoginScreen'),
  '/register/': named(() => import('@/features/auth/AuthScreens'), 'RegisterScreen'),
  '/reset-password/': named(() => import('@/features/auth/AuthScreens'), 'ResetPasswordScreen'),
};

const cache = new Map<string, LazyExoticComponent<ComponentType>>();
function screen(path: string, loader: Loader) {
  let c = cache.get(path);
  if (!c) cache.set(path, (c = lazy(() => loader().then((Component) => ({ default: Component })))));
  return c;
}

const normalize = (p: string) => (p.endsWith('/') ? p : `${p}/`);

setPrefetcher((path) => void (SHELL[normalize(path)] ?? AUTH[normalize(path)])?.());

function PageFallback() {
  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <Skeleton width={220} height={36} />
      <Skeleton height={240} radius={16} />
    </div>
  );
}

function NotFound() {
  const { t } = useApp();
  return (
    <EmptyState
      icon={<IconGrid />}
      title={t('common.not_found')}
      action={
        <Link href="/" className={buttonClass('primary')}>
          {t('tickets.browse')}
        </Link>
      }
    />
  );
}

export function App() {
  const path = normalize(usePathname());
  const { t } = useApp();
  useEffect(() => {
    document.title = 'Edil · እድል';
  }, [path, t]);

  if (AUTH[path]) {
    const Screen = screen(path, AUTH[path]);
    return (
      <Suspense fallback={null}>
        <Screen />
      </Suspense>
    );
  }
  const loader = SHELL[path];
  const Screen = loader ? screen(path, loader) : null;
  return (
    <AppShell>
      <Suspense fallback={<PageFallback />}>{Screen ? <Screen /> : <NotFound />}</Suspense>
    </AppShell>
  );
}
